import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

export const PricingSection: React.FC = () => {
  const { t } = useTranslation()
  const pricing = t('pricing', { returnObjects: true }) as any
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly')

  const redirectToApp = (planId: string) => {
    const mainAppUrl = import.meta.env.VITE_MAIN_APP_URL || 'https://app.nazwolab.pl'
    window.location.href = mainAppUrl
  }

  const getPrice = (planId: string) => {
    const plan = pricing.plans[planId]
    if (planId === 'free') return plan.price

    const prices = {
      basic: { monthly: '30 zł', yearly: '300 zł' },
      enterprise: { monthly: '500 zł', yearly: '5000 zł' }
    }

    return prices[planId as keyof typeof prices][billingPeriod]
  }

  const getBillingText = () => {
    return billingPeriod === 'monthly' ? '/miesiąc' : '/rok'
  }

  return (
    <section id="pricing" className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {pricing.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {pricing.subtitle}
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              {pricing.monthly}
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === 'yearly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              {pricing.yearly}
              <span className="ml-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                {pricing.save}
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {Object.entries(pricing.plans).map(([planId, plan]: [string, any]) => {
            const isPopular = planId === 'basic'
            const isFree = planId === 'free'

            return (
              <div
                key={planId}
                className={`relative rounded-2xl border-2 bg-white p-8 shadow-lg ${
                  isPopular
                    ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-20 scale-105'
                    : 'border-gray-200'
                }`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="flex items-center bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      ⭐ {pricing.popular}
                    </div>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900">
                      {getPrice(planId)}
                    </span>
                    {!isFree && (
                      <span className="text-gray-500 ml-1">
                        {getBillingText()}
                      </span>
                    )}
                  </div>
                  {billingPeriod === 'yearly' && !isFree && (
                    <p className="text-sm text-gray-500 mt-1">
                      {pricing.billedYearly}
                    </p>
                  )}
                  <p className="text-gray-600 mt-2">{plan.description}</p>
                </div>

                {/* Features List */}
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature: string, index: number) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-3 mt-0.5">✓</span>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <button
                  onClick={() => redirectToApp(planId)}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                    isPopular
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                      : isFree
                      ? 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {isFree ? pricing.getStarted : pricing.selectPlan}
                </button>

                {/* Free Trial Notice */}
                {!isFree && (
                  <p className="text-xs text-gray-500 text-center mt-4">
                    {pricing.freeTrialText}
                  </p>
                )}
              </div>
            )
          })}
        </div>

        {/* Money Back Guarantee */}
        <div className="text-center mt-12 p-6 bg-blue-50 rounded-lg max-w-2xl mx-auto">
          <h4 className="font-semibold text-gray-900 mb-2">
            14-dniowa gwarancja zwrotu pieniędzy
          </h4>
          <p className="text-sm text-gray-600">
            Wypróbuj dowolny płatny plan bez ryzyka. Niezadowolony? Otrzymasz pełny zwrot w ciągu 14 dni.
          </p>
        </div>
      </div>
    </section>
  )
}